import { ChangeEvent, useMemo, useRef } from "react";
import styles from "./SettingsAccountPage.module.css";
import { useSettingsProfileViewModel } from "@/features/settings/viewModal/useSettingsProfileViewModel";

export const SettingsAccountPage = () => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const {
    form: { profile, isEdit },
    ui,
    actions,
  } = useSettingsProfileViewModel();

  const displayName = useMemo(() => {
    const fullName = `${profile.firstName} ${profile.lastName}`.trim();
    return fullName || "User";
  }, [profile.firstName, profile.lastName]);

  const initials = useMemo(() => {
    const firstInitial = profile.firstName.trim().charAt(0);
    const lastInitial = profile.lastName.trim().charAt(0);
    return `${firstInitial}${lastInitial}`.toUpperCase() || "U";
  }, [profile.firstName, profile.lastName]);

  const onAvatarButtonClick = () => {
    if (!isEdit || ui.uploadingAvatar) {
      return;
    }
    fileInputRef.current?.click();
  };

  const onAvatarSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    void actions.uploadAvatar(file);
    event.target.value = "";
  };

  const onEditClick = () => {
    if (isEdit) {
      void actions.saveProfile();
      return;
    }
    actions.startEdit();
  };

  return (
    <div className={styles.accountPage}>
      <section className={styles.profileHeader}>
        <div className={styles.profileHeaderLeft}>
          <button
            type="button"
            className={styles.avatarButton}
            onClick={onAvatarButtonClick}
            disabled={!isEdit || ui.uploadingAvatar}
          >
            {profile.avatarUrl ? (
              <img src={profile.avatarUrl} alt={displayName} className={styles.avatarImage} />
            ) : (
              <div className={styles.avatar}>{initials}</div>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className={styles.hiddenInput}
            onChange={onAvatarSelected}
          />
          <div>
            <h1 className={styles.name}>{displayName}</h1>
            <p className={styles.email}>{profile.email || "-"}</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          {isEdit && (
            <button type="button" className={styles.cancelBtn} onClick={actions.cancelEdit}>
              Cancel
            </button>
          )}
          <button
            type="button"
            className={styles.editBtn}
            onClick={onEditClick}
            disabled={ui.loading || ui.uploadingAvatar}
          >
            {isEdit ? "Save" : "Edit"}
          </button>
        </div>
      </section>

      {ui.errorMessage && (
        <section className={styles.errorBox} role="alert">
          {ui.errorMessage}
        </section>
      )}

      <section className={styles.panel}>
        <div className={styles.panelInner}>
          <div>
            <h2 className={styles.formTitle}>Profile</h2>

            <div className={styles.field}>
              <span className={styles.label}>
                First name
                {isEdit && (
                  <span className={styles.requiredMark} aria-hidden="true">
                    {" "}
                    *
                  </span>
                )}
              </span>
              {isEdit ? (
                <>
                  <input
                    value={profile.firstName}
                    onChange={(event) => actions.onFirstNameChange(event.target.value)}
                    className={`${styles.input} ${ui.firstNameError ? styles.inputError : ""}`}
                    placeholder="First name"
                    required
                    aria-required="true"
                    aria-invalid={Boolean(ui.firstNameError)}
                    aria-describedby={ui.firstNameError ? "first-name-error" : undefined}
                  />
                  {ui.firstNameError && (
                    <span id="first-name-error" className={styles.fieldError} role="alert">
                      {ui.firstNameError}
                    </span>
                  )}
                </>
              ) : (
                <div className={styles.value}>{profile.firstName || "-"}</div>
              )}
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Last name</span>
              {isEdit ? (
                <input
                  value={profile.lastName}
                  onChange={(event) => actions.onLastNameChange(event.target.value)}
                  className={styles.input}
                  placeholder="Last name"
                />
              ) : (
                <div className={styles.value}>{profile.lastName || "-"}</div>
              )}
            </div>

            <div className={styles.gridTwo}>
              <div className={styles.field}>
                <span className={styles.label}>User email</span>
                <div className={styles.value}>{profile.email || "-"}</div>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>Phone Number</span>
                {/* {isEdit ? (
                  <input
                    value={profile.phone}
                    onChange={(event) => actions.onPhoneChange(event.target.value)}
                    className={styles.input}
                    placeholder="Phone number"
                  />
                ) : ( */}
                <div className={styles.value}>{profile.phone || "-"}</div>
                {/* )} */}
              </div>
            </div>

            <div className={styles.field}>
              <span className={styles.label}>Mailing Address</span>
              {isEdit ? (
                <input
                  value={profile.address}
                  disabled={true}
                  onChange={(event) => actions.onAddressChange(event.target.value)}
                  className={styles.input}
                  placeholder="Mailing address"
                />
              ) : (
                <div className={styles.value}>{profile.address || "-"}</div>
              )}
            </div>
          </div>
        </div>
        <hr className={styles.divider} />
      </section>
    </div>
  );
};
